import { useQuery } from '@tanstack/react-query';
import { makeApiCall, getAuthHeaders } from '../../utils/apiConfig';
import { apiService } from '../../services/api';

export interface OrganizationStats {
  totalOrganizations: number;
  cameraStores: number;
  vendors: number;
  /** Vendor organizations in `active` status. */
  activeVendors?: number;
  activeOrganizations: number;
  pendingVerification: number;
  suspendedOrganizations: number;
  newThisWeek: number;
  newThisMonth: number;
}

export interface AdminSystemStats {
  stats?: {
    totalUsers?: number;
    newUsersThisWeek?: number;
    totalProducts?: number;
    totalCameras?: number;
  };
  totalUsers?: number;
  totalProducts?: number;
  totalCameras?: number;
}

export interface AdminBillingStats {
  activeSubscriptions?: number;
  payingSubscriptions?: number;
  monthlyRevenue?: number;
  storeCount?: number;
  storesWithoutPaidPlan?: number;
}

export interface StagedProductStats {
  pending: number;
  remediating: number;
  verifying: number;
  review: number;
  approved: number;
  promoted: number;
  rejected: number;
  failed: number;
  oldestPendingAgeDays: number | null;
  oldestReviewAgeDays: number | null;
}

export interface AdminJob {
  id: string;
  status: string;
  brandName?: string;
  createdAt: string;
  startedAt?: string;
  completedAt?: string;
  result?: { productsCreated?: number; errors?: string[] };
  progress?: { current: number; total: number };
}

export interface VendorApprovalPending {
  id: string;
  vendorName: string;
  brandName: string;
  skuCount: number;
  submittedAt: string;
}

export interface LatencyStats {
  p95Ms: number | null;
  sampleCount: number;
}

export function useAdminSystemStats(enabled: boolean) {
  return useQuery<AdminSystemStats | null>({
    queryKey: ['admin-dashboard-stats'],
    queryFn: () => apiService.getSystemStats(),
    enabled,
  });
}

export function useOrganizationStats(enabled: boolean) {
  return useQuery<OrganizationStats | null>({
    queryKey: ['organization-stats'],
    queryFn: async () => {
      const headers = await getAuthHeaders();
      const response = await makeApiCall('/organizations/stats', { headers });
      if (!response.ok) throw new Error(`Failed to load organization stats (${response.status})`);
      const body = await response.json();
      return body?.data || null;
    },
    enabled,
  });
}

export function useAdminBillingStats(enabled: boolean) {
  return useQuery<AdminBillingStats | null>({
    queryKey: ['admin-billing-stats'],
    queryFn: async () => {
      const headers = await getAuthHeaders();
      const response = await makeApiCall('/billing/admin/stats', { headers });
      if (!response.ok) return null;
      const body = await response.json();
      return body?.data || null;
    },
    enabled,
  });
}

export function useStagedProductStats(enabled: boolean) {
  return useQuery<StagedProductStats | null>({
    queryKey: ['staged-product-stats'],
    queryFn: async () => {
      const headers = await getAuthHeaders();
      const response = await makeApiCall('/staged-products/stats', { headers });
      if (!response.ok) throw new Error(`Failed to load staged product stats (${response.status})`);
      const body = await response.json();
      return body?.data || null;
    },
    enabled,
  });
}

/** Vendor data-ingestion jobs (scraper/API runs that actually pull products in) from the
 *  `ingestionJobs` collection. Used for both the "failed (24h)" KPI/attention item and the
 *  weekly ingestion chart. This is a different store than the generic `/admin/jobs` list
 *  (customer_sync, admin_operation, etc.) — real vendor ingestion runs are never written
 *  there, so pulling from `/admin/jobs` left the chart empty. Passing `since` (rather than a
 *  fixed `limit`) tells the endpoint to page through the full date window server-side, so a
 *  busy period can't silently push older weeks off the end of a guessed row cap. `view=summary`
 *  asks for the chart fields only, not the job document's logs and scrape cursors. */
export function useAdminRecentJobs(enabled: boolean, hours = 24 * 7 * 12) {
  return useQuery<AdminJob[]>({
    queryKey: ['admin-recent-ingestion-jobs', hours],
    queryFn: async () => {
      const since = new Date(Date.now() - hours * 60 * 60 * 1000).toISOString();
      const headers = await getAuthHeaders();
      const response = await makeApiCall(
        `/ingestion/jobs?since=${encodeURIComponent(since)}&view=summary`,
        { headers },
      );
      if (!response.ok) throw new Error(`Failed to load ingestion jobs (${response.status})`);
      const body = await response.json();
      return (body?.data || []) as AdminJob[];
    },
    enabled,
  });
}

export function useVendorApprovalPending(enabled: boolean) {
  return useQuery<VendorApprovalPending[]>({
    queryKey: ['vendor-approval-pending'],
    queryFn: async () => {
      const headers = await getAuthHeaders();
      const response = await makeApiCall('/vendor-approval/pending', { headers });
      if (!response.ok) throw new Error(`Failed to load pending vendor approvals (${response.status})`);
      const body = await response.json();
      return body?.data || [];
    },
    enabled,
  });
}

export function useApiLatency(enabled: boolean) {
  return useQuery<LatencyStats>({
    queryKey: ['admin-system-latency'],
    queryFn: async () => {
      const headers = await getAuthHeaders();
      const response = await makeApiCall('/admin/system/latency', { headers });
      if (!response.ok) return { p95Ms: null, sampleCount: 0 };
      const body = await response.json();
      return body?.data || { p95Ms: null, sampleCount: 0 };
    },
    enabled,
    refetchInterval: 5 * 60 * 1000,
  });
}
